import { memo, useCallback, useState } from 'react';
import { useTranslation } from '@aimsos/i18n';
import { Button, Input } from '@aimsos/ui';

import { Send } from 'lucide-react';

/**
 * The comment box, pinned under the thread (CT-5528).
 *
 * `onSubmit` resolves to whether the comment was posted: only then is the box
 * cleared, so a failed post keeps what the user typed. Enter submits; the button
 * is disabled while the box is empty or a post is in flight.
 */
const NoteCommentForm = memo(function NoteCommentForm({ onSubmit, submitting }) {
  const { t } = useTranslation('unified-profiles');
  const [text, setText] = useState('');

  const canSubmit = text.trim() !== '' && !submitting;

  const submit = useCallback(async () => {
    if (!canSubmit) return;
    const posted = await onSubmit(text);
    if (posted) setText('');
  }, [canSubmit, onSubmit, text]);

  const handleChange = useCallback((event) => setText(event.target.value), []);
  const handleKeyDown = useCallback(
    (event) => {
      if (event.key !== 'Enter') return;
      event.preventDefault();
      submit();
    },
    [submit],
  );

  return (
    <div className="flex items-end gap-2" data-testid="note-comment-form">
      <div className="min-w-0 flex-1">
        <Input
          aria-label={t('notes.comments.inputLabel')}
          placeholder={t('notes.comments.placeholder')}
          value={text}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          disabled={submitting}
        />
      </div>
      <Button variant="primary" size="md" leftIcon={Send} onClick={submit} disabled={!canSubmit}>
        {t('notes.comments.send')}
      </Button>
    </div>
  );
});

export default NoteCommentForm;
